import { diaNegocio, formatoHora12, inicioSemana, sumarDias, type FilaHorarioInput, type SemanaHorarios } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarX2, ChevronLeft, ChevronRight, Copy, Palmtree } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api, ErrorApi, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tarjeta } from '@/components/ui/data';
import { Campo, Input, Switch, InputHora } from '@/components/ui/form';
import { Popover, PopoverContenido, PopoverDisparador } from '@/components/ui/overlay';
import { cn, diaLargo } from '@/lib/utils';
import { avisarError } from '@/lib/avisos';

type Celda = SemanaHorarios['barberos'][number]['dias'][string];

function EditorCelda({ barberoId, dia, celda, onGuardar, guardando, onCancelarHorario, cancelando }: {
  barberoId: string;
  dia: string;
  celda: Celda;
  onGuardar: (fila: FilaHorarioInput) => void;
  guardando: boolean;
  /** Deja el día sin asignar; sus citas pendientes quedan en «Por asignar». */
  onCancelarHorario: () => void;
  cancelando: boolean;
}) {
  const h = celda.horario;
  const [descanso, setDescanso] = useState(h?.esDescanso ?? false);
  const [entrada, setEntrada] = useState(h?.entrada ?? '10:00');
  const [salida, setSalida] = useState(h?.salida ?? '21:00');
  const [comidaInicio, setComidaInicio] = useState(h?.comidaInicio ?? '14:00');
  const [comidaFin, setComidaFin] = useState(h?.comidaFin ?? '15:00');
  const [conComida, setConComida] = useState(h ? !!h.comidaInicio : true);

  return (
    <div className="flex w-64 flex-col gap-3">
      <p className="font-display text-[15px] first-letter:uppercase">{diaLargo(dia)}</p>
      {celda.vacacionAutorizada && (
        <p className="rounded-md bg-warning-soft px-2 py-1.5 text-[12.5px] text-warning">Vacaciones autorizadas: solo el dueño puede asignarle horario.</p>
      )}
      <label className="flex items-center justify-between text-[13px]">
        Día de descanso <Switch checked={descanso} onCheckedChange={setDescanso} />
      </label>
      {!descanso && (
        <>
          <div className="grid gap-2">
            <Campo etiqueta="Entrada">{(p) => <InputHora {...p} value={entrada} onChange={setEntrada} />}</Campo>
            <Campo etiqueta="Salida">{(p) => <InputHora {...p} value={salida} onChange={setSalida} />}</Campo>
          </div>
          <label className="flex items-center justify-between text-[13px]">
            Hora de comida <Switch checked={conComida} onCheckedChange={setConComida} />
          </label>
          {conComida && (
            <div className="grid gap-2">
              <Campo etiqueta="Inicia">{(p) => <InputHora {...p} value={comidaInicio} onChange={setComidaInicio} />}</Campo>
              <Campo etiqueta="Termina">{(p) => <InputHora {...p} value={comidaFin} onChange={setComidaFin} />}</Campo>
            </div>
          )}
        </>
      )}
      <Button
        cargando={guardando}
        onClick={() =>
          onGuardar({
            barberoId,
            dia,
            esDescanso: descanso,
            ...(descanso ? {} : { entrada, salida, ...(conComida ? { comidaInicio, comidaFin } : {}) }),
          })
        }
      >
        Guardar
      </Button>
      {h && (
        <div className="border-t border-border pt-3">
          <Button variante="secundario" className="w-full text-destructive" onClick={onCancelarHorario} cargando={cancelando}>
            <CalendarX2 /> Cancelar horario
          </Button>
          <p className="mt-1.5 text-[12px] text-foreground-muted">El día queda sin asignar. Si tenía citas, pasan a «Por asignar» para reasignarlas.</p>
        </div>
      )}
    </div>
  );
}

export function HorariosPage() {
  const { puede } = useSesion();
  const qc = useQueryClient();
  const [semana, setSemana] = useState(inicioSemana(sumarDias(diaNegocio(new Date()), 0), 1));
  const consulta = useQuery({ queryKey: ['horarios', semana], queryFn: () => api<SemanaHorarios>('/horarios', { query: { semana } }) });
  const editable = puede('personal.horarios');
  const hoy = diaNegocio(new Date());

  const guardar = useMutation({
    mutationFn: (v: { fila: FilaHorarioInput; solicitarAlDueno?: boolean }) =>
      api<{ guardados: number; aprobacionId?: string }>('/horarios/semana', { method: 'PUT', body: { semana, filas: [v.fila], solicitarAlDueno: v.solicitarAlDueno } }),
    onSuccess: (r) => {
      if (r.aprobacionId) return toast.success('Solicitud enviada al dueño. El horario se guardará cuando la apruebe.');
      toast.success('Horario guardado');
      void qc.invalidateQueries({ queryKey: ['horarios'] });
      void qc.invalidateQueries({ queryKey: ['barberos'] });
      void qc.invalidateQueries({ queryKey: ['agenda'] });
    },
    onError: (e, v) => {
      if (e instanceof ErrorApi && e.code === 'REQUIERE_DUENO') {
        toast.warning(e.message, { duration: 10_000, action: { label: 'Enviar al dueño', onClick: () => guardar.mutate({ ...v, solicitarAlDueno: true }) } });
      } else avisarError(e);
    },
  });
  const cancelarHorario = useMutation({
    mutationFn: (v: { barberoId: string; dia: string }) =>
      api<{ citasDesasignadas: number }>(`/horarios/${v.barberoId}/${v.dia}`, { method: 'DELETE' }),
    onSuccess: (r) => {
      toast.success(
        r.citasDesasignadas
          ? `Horario cancelado. ${r.citasDesasignadas} ${r.citasDesasignadas === 1 ? 'cita quedó' : 'citas quedaron'} en «Por asignar» de la agenda.`
          : 'Horario cancelado: el día quedó sin asignar.',
      );
      for (const clave of ['horarios', 'barberos', 'agenda', 'pendientes']) void qc.invalidateQueries({ queryKey: [clave] });
    },
    onError: (e) => avisarError(e),
  });
  const copiar = useMutation({
    mutationFn: () => api<{ copiados: number; omitidos: string[] }>('/horarios/copiar', { method: 'POST', body: { desde: semana, hacia: sumarDias(semana, 7) } }),
    onSuccess: (r) => {
      toast.success(`Se copiaron ${r.copiados} horarios a la siguiente semana${r.omitidos.length ? ` (${r.omitidos.length} omitidos por vacaciones)` : ''}`);
      setSemana(sumarDias(semana, 7));
      void qc.invalidateQueries({ queryKey: ['horarios'] });
      void qc.invalidateQueries({ queryKey: ['barberos'] });
    },
    onError: (e) => avisarError(e),
  });

  const d = consulta.data;
  return (
    <ModuleLayout titulo="Personal" secciones={SECCIONES_POR_MODULO.personal}>
      <EncabezadoPagina
        titulo="Horarios semanales"
        descripcion="Define quién trabaja cada día; la agenda solo ofrece horarios de quien tiene turno."
        acciones={
          editable && (
            <Button variante="secundario" onClick={() => copiar.mutate()} cargando={copiar.isPending}>
              <Copy /> Copiar a la siguiente semana
            </Button>
          )
        }
      />
      <div className="mb-4 flex items-center gap-2">
        <Button variante="secundario" tamano="icono" onClick={() => setSemana(sumarDias(semana, -7))} aria-label="Semana anterior">
          <ChevronLeft />
        </Button>
        <p className="min-w-56 text-center text-[14px] font-medium first-letter:uppercase">Semana del {diaLargo(semana)}</p>
        <Button variante="secundario" tamano="icono" onClick={() => setSemana(sumarDias(semana, 7))} aria-label="Semana siguiente">
          <ChevronRight />
        </Button>
      </div>

      {!d ? (
        <Esqueleto className="h-64" />
      ) : (
        <Tarjeta className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-[13px]">
            <thead>
              <tr>
                <th className="border-b border-border bg-surface-alt px-3 py-2 text-left text-[12px] font-semibold uppercase tracking-wide text-foreground-muted">Barbero</th>
                {d.dias.map((dia) => (
                  <th key={dia} className={cn('border-b border-border bg-surface-alt px-2 py-2 text-left text-[12px] font-semibold text-foreground-muted', dia === hoy && 'text-accent')}>
                    <span className="block uppercase tracking-wide">{diaLargo(dia).split(',')[0]}</span>
                    <span className="cifras font-display text-[16px] text-foreground">{dia.slice(8)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.barberos.map((b) => (
                <tr key={b.id}>
                  <td className="border-b border-border px-3 py-2 font-medium">{b.nombre}</td>
                  {d.dias.map((dia) => {
                    const celda = b.dias[dia]!;
                    const h = celda.horario;
                    const contenido = (
                      <span className={cn('flex min-h-14 flex-col justify-center rounded-md border px-2 py-1.5 text-left', h ? 'border-border bg-surface' : 'border-dashed border-border text-foreground-muted')}>
                        {h ? (
                          h.esDescanso ? (
                            <span className="text-foreground-muted">Descanso</span>
                          ) : (
                            <>
                              <span className="cifras font-medium">
                                {formatoHora12(h.entrada ?? '')} – {formatoHora12(h.salida ?? '')}
                              </span>
                              {h.comidaInicio && <span className="cifras text-[11.5px] text-foreground-muted">comida {formatoHora12(h.comidaInicio)}</span>}
                            </>
                          )
                        ) : (
                          <span>Sin horario</span>
                        )}
                        {celda.vacacionAutorizada && (
                          <Insignia tono="aviso" className="mt-1 w-fit">
                            <Palmtree /> Vacaciones autorizadas
                          </Insignia>
                        )}
                      </span>
                    );
                    return (
                      <td key={dia} className="border-b border-border p-1.5 align-top">
                        {editable ? (
                          <Popover>
                            <PopoverDisparador asChild>
                              <button type="button" className="w-full hover:[&>span]:border-accent" aria-label={`Horario de ${b.nombre} el ${diaLargo(dia)}`}>
                                {contenido}
                              </button>
                            </PopoverDisparador>
                            <PopoverContenido>
                              <EditorCelda barberoId={b.id} dia={dia} celda={celda} onGuardar={(f) => guardar.mutate({ fila: f })}
                                guardando={guardar.isPending}
                                onCancelarHorario={() => cancelarHorario.mutate({ barberoId: b.id, dia })}
                                cancelando={cancelarHorario.isPending && cancelarHorario.variables?.barberoId === b.id && cancelarHorario.variables.dia === dia}
                              />
                            </PopoverContenido>
                          </Popover>
                        ) : (
                          contenido
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Tarjeta>
      )}

      {d && d.pendientes.length > 0 && (
        <Tarjeta className="mt-4 p-4">
          <p className="font-display text-[16px]">Citas de esta semana sin barbero ({d.pendientes.length})</p>
          <p className="mb-2 text-[12.5px] text-foreground-muted">Tómalas en cuenta al asignar turnos (se muestra el barbero de preferencia).</p>
          <ul className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {d.pendientes.map((p) => (
              <li key={p.citaId} className="rounded-md bg-surface-alt px-3 py-2 text-[13px]">
                <span className="cifras first-letter:uppercase">{diaLargo(p.dia).split(',')[0]} {formatoHora12(p.hora)}</span> · {p.cliente}
                {p.barberoPreferidoId && (
                  <span className="block text-[12px] text-foreground-muted">
                    Prefiere a {d.barberos.find((b) => b.id === p.barberoPreferidoId)?.nombre ?? 'otro barbero'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}
    </ModuleLayout>
  );
}
